import { PARTNER_TYPES } from '../../partner-types';
import { MenuItem } from '../types';

/**
 * The "Integration Guide" sidebar section.
 *
 * BMW has a dedicated `BMW Fleet` sidebar section in `sections/bmw.ts`. Every
 * submenu here excludes the `bmwfleet` user group because the generic rydpay,
 * Connect and SDK docs describe a different integration and would mislead BMW
 * readers.
 *
 * `excludedPartners` is matched against the user's single `userGroup`
 * (`navmenu.vue` → `isNotExcludedPartner`), which is why the group name and not
 * a partner type is listed. **A new submenu added to this array must exclude
 * `'bmwfleet'` too**, otherwise it appears in BMW's sidebar.
 */
export const integrationGuideSection: MenuItem[] = [
  {
    headTitle1: 'Integration Guide',
    headTitle2: 'How to integrate ryd',
    type: 'headtitle',
    requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER, PARTNER_TYPES.INDIVIDUAL_PARTNER],
    excludedPartners: ['bosch', 'oilpay', 'mobiko'],
  },
  {
    title: 'Integration Overview',
    icon: 'stroke-widget',
    iconf: 'fill-widget',
    type: 'sub',
    active: false,
    requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
    excludedPartners: ['bosch', 'bmwfleet'],
    children: [
      {
        path: '/integration-overview',
        title: 'Integration Overview',
        type: 'link',
        active: false,
      },
      {
        path: '/integration-guide/network-and-security',
        title: 'Network & Security',
        type: 'link',
        active: false,
      },
    ],
  },
  {
    title: 'rydpay APIs',
    icon: 'stroke-api-integration-widget',
    iconf: 'fill-api-integration-widget',
    type: 'sub',
    active: false,
    requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
    excludedPartners: ['bosch', 'bmwfleet'],
    children: [
      {
        path: '/integration-guide/rydpay-apis/getting-started',
        title: 'Getting Started',
        type: 'link',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
      },
      {
        path: '/integration-guide/rydpay-apis/integration-pathways',
        title: 'Integration Pathways',
        type: 'link',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
      },
      {
        title: 'Guides: General',
        type: 'sub',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
        children: [
          {
            path: '/integration-guide/rydpay-apis/key-concepts',
            title: 'Key Concepts',
            type: 'link',
            active: false,
            requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          },
          {
            path: '/integration-guide/rydpay-apis/api-fundamentals',
            title: 'API Fundamentals',
            type: 'link',
            active: false,
            requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          },
          {
            path: '/integration-guide/rydpay-apis/best-practices',
            title: 'Best Practices',
            type: 'link',
            active: false,
            requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          },
          {
            path: '/integration-guide/rydpay-apis/order-state-management',
            title: 'Order State Management',
            type: 'link',
            active: false,
            requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          },
          {
            path: '/integration-guide/rydpay-apis/order-timeouts-and-cancellation',
            title: 'Order Timeouts & Cancellation',
            type: 'link',
            active: false,
            requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          },

          {
            path: '/integration-guide/rydpay-apis/places-filtering-guide',
            title: 'Places Filtering Guide',
            type: 'link',
            active: false,
            requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          },
          {
            path: '/integration-guide/rydpay-apis/webhooks',
            title: 'Webhooks',
            type: 'link',
            active: false,
            requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          },
          {
            path: '/integration-guide/rydpay-apis/testing-and-simulation',
            title: 'Testing & Simulation',
            type: 'link',
            active: false,
            requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          },
        ],
      },
      {
        title: 'Guides: Fuelling',
        type: 'sub',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
        children: [
          {
            path: '/integration-guide/rydpay-apis/guides-fuelling/prepay-and-postpay',
            title: 'Prepay vs Postpay',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/rydpay-apis/guides-fuelling/fuelling-client-flow',
            title: 'Client Flow',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/rydpay-apis/testing-and-simulation/fuelling-test-scenarios',
            title: 'Test Scenarios',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/rydpay-apis/guides-fuelling/fuelling-postman-collection-viewer',
            title: 'Postman Collection Viewer',
            type: 'link',
            active: false,
            requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          },
          {
            path: '/integration-guide/rydpay-apis/testing-and-simulation/fuelling-simulated-stations',
            title: 'Simulated Stations',
            type: 'link',
            active: false,
          },
        ],
      },
      {
        title: 'Guides: Car Wash',
        type: 'sub',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
        children: [
          {
            path: '/integration-guide/rydpay-apis/testing-and-simulation/car-wash-test-scenarios',
            title: 'Test Scenarios',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/rydpay-apis/testing-and-simulation/car-wash-simulated-stations',
            title: 'Simulated Stations',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/rydpay-apis/car-wash-client-flow',
            title: 'Client Flow',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/rydpay-apis/car-wash-demo-and-playground',
            title: 'Demo & Playground',
            type: 'link',
            active: false,
          },
        ],
      },
      {
        title: 'Guides: EV Charging',
        type: 'sub',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
        children: [
          {
            path: '/integration-guide/rydpay-apis/guides-ev-charging/ev-insights-and-statistics',
            title: 'EV Insights & Statistics',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/rydpay-apis/guides-ev-charging/api-journey',
            title: 'API Journey',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/rydpay-apis/guides-ev-charging/ev-pricing',
            title: 'EV Pricing',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/rydpay-apis/testing-and-simulation/ev-charging-test-scenarios',
            title: 'Test Scenarios',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/rydpay-apis/testing-and-simulation/ev-charging-simulated-stations',
            title: 'Simulated Stations',
            type: 'link',
            active: false,
          },
        ],
      },
      {
        title: 'Action Codes',
        type: 'sub',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
        children: [
          {
            path: '/integration-guide/rydpay-apis/action-codes',
            title: 'Overview',
            type: 'link',
            active: false,
            requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          },
          {
            path: '/integration-guide/rydpay-apis/action-codes/search',
            title: 'Search Action Codes',
            type: 'link',
            active: false,
            requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          },
          {
            path: '/integration-guide/rydpay-apis/action-codes/pop',
            title: 'POP Action Codes',
            type: 'link',
            active: false,
            requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          },
          {
            path: '/integration-guide/rydpay-apis/action-codes/pay',
            title: 'PAY Action Codes',
            type: 'link',
            active: false,
            requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          },
          {
            path: '/integration-guide/rydpay-apis/action-codes/solutions',
            title: 'Solutions & Scenarios',
            type: 'link',
            active: false,
            requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
          },
        ],
      },
      {
        path: '/integration-guide/rydpay-apis/api-reference',
        title: 'ryd.pay API Reference',
        type: 'link',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
      },
      {
        title: 'Support & Resources',
        type: 'sub',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
        children: [
          {
            path: '/integration-guide/rydpay-apis/terms-and-definitions',
            title: 'Terms & Definitions',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/rydpay-apis/faqs',
            title: 'FAQs',
            type: 'link',
            active: false,
          },
        ],
      },
      {
        path: '/integration-guide/rydpay-apis/release-notes-and-updates',
        title: 'Release Notes & Updates',
        type: 'link',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
      },
    ],
  },
  {
    title: 'Fleet APIs',
    icon: 'stroke-fleet-administration',
    iconf: 'fill-fleet-administration',
    type: 'sub',
    active: false,
    requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
    excludedPartners: ['bmwfleet'],
    children: [
      {
        path: '/integration-guide/fleet-apis/fleet-api-introduction',
        title: 'Fleet API Introduction',
        type: 'link',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
      },
      {
        path: '/integration-guide/fleet-apis/fleet-api-authentication',
        title: 'Fleet API Authentication',
        type: 'link',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
      },
      {
        path: '/integration-guide/fleet-apis/fleet-api-app-login',
        title: 'Login to ryd',
        type: 'link',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
      },
      {
        path: '/integration-guide/fleet-apis/fleet-api-reference',
        title: 'Fleet API Reference',
        type: 'link',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
      },
    ],
  },
  {
    title: 'Partner Wallet',
    type: 'sub',
    icon: 'wallet',
    iconf: 'fill-webapp',
    active: false,
    requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
    excludedPartners: ['bmwfleet'],
    children: [
      {
        path: '/integration-guide/rydpay-apis/partner-wallet',
        title: 'Partner Wallet Integration',
        type: 'link',
        active: false,
        requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
      },
    ],
  },
  {
    title: 'ryd Connect',
    icon: 'stroke-webview',
    iconf: 'fill-webview',
    type: 'sub',
    active: false,
    requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
    excludedPartners: ['bosch', 'bmwfleet'],
    badge: 'Recommended',
    badgeColor: 'success',
    children: [
      {
        title: 'Getting Started',
        type: 'sub',
        active: false,
        children: [
          {
            path: '/integration-guide/ryd-connect/getting-started/overview',
            title: 'Overview',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/ryd-connect/getting-started/integration-pathways',
            title: 'Integration Pathways',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/ryd-connect/getting-started/webview-approaches',
            title: 'WebView Approaches',
            type: 'link',
            active: false,
          },
        ],
      },
      {
        title: 'Products & Payload',
        type: 'sub',
        active: false,
        children: [
          {
            path: '/integration-guide/ryd-connect/payload/products',
            title: 'Products',
            type: 'link',
            active: false,
            badgeType: 'success',
            badgeValue: 'New',
          },
          {
            path: '/integration-guide/ryd-connect/payload/partner-extra-data',
            title: 'Partner Extra Data',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/ryd-connect/payload/skip-the-map',
            title: 'Skip the Map',
            type: 'link',
            active: false,
          },
        ],
      },
      {
        title: 'Authentication Flows',
        type: 'sub',
        active: false,
        children: [
          {
            path: '/integration-guide/ryd-connect/authentication',
            title: 'Overview & Decision',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/ryd-connect/authentication/session-token',
            title: 'Session Token',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/ryd-connect/authentication/jwt-assertion',
            title: 'JWT Assertion',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/ryd-connect/authentication/one-time-token',
            title: 'One-Time Token Security',
            type: 'link',
            active: false,
          },
        ],
      },
      {
        title: 'App Integration',
        type: 'sub',
        active: false,
        children: [
          {
            path: '/integration-guide/ryd-connect/app-integration/callbacks',
            title: 'Callbacks & Events',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/ryd-connect/app-integration/theming',
            title: 'Partner Theming',
            type: 'link',
            active: false,
          },
        ],
      },
      {
        title: 'Testing & Resources',
        type: 'sub',
        active: false,
        children: [
          {
            path: '/integration-guide/ryd-connect/testing/guide',
            title: 'Testing',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/ryd-connect/testing/simulated-stations',
            title: 'Simulated Stations',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/ryd-connect/testing/developer-resources',
            title: 'Developer Resources',
            type: 'link',
            active: false,
          },
        ],
      },
    ],
  },
  {
    title: 'Android Automotive OS',
    icon: 'stroke-maps',
    iconf: 'fill-maps',
    type: 'sub',
    active: false,
    requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
    excludedPartners: ['bosch', 'bmwfleet'],
    badge: 'New',
    badgeColor: 'success',
    children: [
      {
        title: 'Getting Started',
        type: 'sub',
        active: false,
        children: [
          {
            path: '/integration-guide/android-automotive/getting-started/overview',
            title: 'Overview',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/android-automotive/getting-started/platform-basics',
            title: 'Platform Basics',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/android-automotive/getting-started/integration-pathways',
            title: 'Integration Pathways',
            type: 'link',
            active: false,
          },
        ],
      },
      {
        title: 'In-Car Experience',
        type: 'sub',
        active: false,
        children: [
          {
            path: '/integration-guide/android-automotive/in-car/driver-journey',
            title: 'Driver Journey',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/android-automotive/in-car/vehicle-integration',
            title: 'Vehicle Integration',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/android-automotive/in-car/app-variants-and-stores',
            title: 'App Variants & Stores',
            type: 'link',
            active: false,
          },
        ],
      },
      {
        title: 'Build & Launch',
        type: 'sub',
        active: false,
        children: [
          {
            path: '/integration-guide/android-automotive/launch/best-practices',
            title: 'Best Practices',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/android-automotive/launch/testing',
            title: 'Testing & Checklist',
            type: 'link',
            active: false,
          },
          {
            path: '/integration-guide/android-automotive/launch/resources-and-faq',
            title: 'Resources & FAQ',
            type: 'link',
            active: false,
          },
        ],
      },
    ],
  },
  {
    title: 'Webapp Integration (Legacy)',
    icon: 'stroke-webapp',
    iconf: 'fill-webapp',
    type: 'sub',
    active: false,
    requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
    excludedPartners: ['bosch', 'bmwfleet'],
    badge: 'Deprecated',
    badgeColor: 'warning',
    children: [
      {
        path: '/integration-guide/webapp-integration/introduction',
        title: 'Introduction',
        type: 'link',
        active: false,
      },
      {
        path: '/integration-guide/webapp-integration/getting-started',
        title: 'Getting Started',
        type: 'link',
        active: false,
      },
      {
        path: '/integration-guide/webapp-integration/topup-flow-steps',
        title: 'Topup Flow Steps',
        type: 'link',
        active: false,
      },
      {
        path: '/integration-guide/webapp-integration/testing',
        title: 'Testing & Support',
        type: 'link',
        active: false,
      },
    ],
  },
  {
    title: 'Registration Deeplink',
    icon: 'stroke-deeplink',
    iconf: 'fill-deeplink',
    type: 'sub',
    active: false,
    requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
    excludedPartners: ['bosch', 'exorigo_upos', 'oilpay', 'mobiko', 'bmwfleet'],
    children: [
      {
        path: '/integration-guide/deeplinking-registration/getting-started',
        title: 'Getting Started',
        type: 'link',
        active: false,
      },
      {
        path: '/integration-guide/deeplinking-registration/api-reference',
        title: 'API Reference',
        type: 'link',
        active: false,
      },
    ],
  },
  {
    title: 'Registration Deeplink',
    icon: 'stroke-deeplink',
    iconf: 'fill-deeplink',
    type: 'sub',
    active: false,
    requiredPartnerTypes: [PARTNER_TYPES.INDIVIDUAL_PARTNER],
    requiredUserGroup: ['freenet'],
    children: [
      {
        path: '/integration-guide/deeplinking-registration/getting-started',
        title: 'Getting Started',
        type: 'link',
        active: false,
      },
      {
        path: '/integration-guide/deeplinking-registration/api-reference',
        title: 'API Reference',
        type: 'link',
        active: false,
      },
    ],
  },
  {
    title: 'SDK Documentation',
    icon: 'stroke-sdk',
    iconf: 'fill-sdk',
    type: 'sub',
    active: false,
    requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER],
    excludedPartners: ['bosch', 'bmwfleet'],
    children: [
      {
        path: '/integration-guide/rydsdk/introduction',
        title: 'Introduction & Benefits',
        type: 'link',
        active: false,
      },
      {
        path: '/integration-guide/rydsdk/getting-started/installation',
        title: 'Available On Demand',
        type: 'link',
        active: false,
      },
    ],
  },
];
